import RfiAxios from '../../http/requests/rfi/index.js';

export default {
    /**
     * Sets RFI component ID.
     *
     * @param commit Vuex commit function
     * @param dispatch Vuex dispatch function
     * @param state Vuex module state
     * @param id RFI ID
     *
     * @returns {Promise<unknown>}
     */
    setRfiId ({ commit, dispatch, state }, id) {
        return new Promise((resolve, reject) => {
            let currentId = state.id;

            commit('SET_RFI_ID', id);

            if (currentId !== id) {
                dispatch('getRfi')
                    .then(response => {
                        resolve(response);
                    })
                    .catch(error => {
                        reject(error);
                    });
            } else {
                resolve();
            }
        });
    },

    /**
     * Retrieves RFI main data, by previously set ID.
     *
     * @param commit Vuex commit function
     * @param state Vuex module state
     *
     * @returns {Promise<unknown>}
     */
    getRfi ({ commit, state }) {
        commit('SET_RFI_LOADING', true);

        return new Promise((resolve, reject) => {
            RfiAxios.getRfi(state.id)
                .then(response => {
                    if (!response.data.success) {
                        reject({ message: response.data.message });
                    }

                    commit('SET_RFI_CONVERSATIONS', response.data.data.conversations);
                    commit('SET_RFI_LOADING', false);

                    resolve(response);
                })
                .catch(error => {
                    commit('SET_RFI_LOADING', false);

                    reject(error);
                });
        });
    },

    /**
     * Resets RFI state to initial one.
     *
     * @param commit Vuex commit function
     */
    resetRfi ({ commit }) {
        commit('RESET_RFI');
    },

    /**
     * Sets search query.
     *
     * @param commit Vuex commit function
     * @param query Search query
     */
    setSearchQuery ({ commit }, query) {
        commit('SET_RFI_SEARCH_QUERY', query);
    },

    /**
     * Retrieves RFI allowed recipients.
     *
     * @param commit Vuex commit function
     * @param state Vuex module state
     *
     * @returns {Promise<unknown>}
     */
    getAllowedRecipients ({ commit, state }) {
        return new Promise((resolve, reject) => {
            RfiAxios.getAllowedRecipients(state.id)
                .then(response => {
                    if (!response.data.success) {
                        reject({ message: response.data.message });
                    }

                    commit('SET_RFI_RECIPIENTS', response.data.data);

                    resolve(response);
                })
                .catch(error => {
                    reject(error);
                });
        });
    },

    /**
     * Get RFI file upload URL.
     *
     * @param commit Vuex commit function
     * @param payload Payload data
     *
     * @returns {Promise<unknown>}
     */
    getFileUploadUrl ({ commit }, payload) {
        return new Promise((resolve, reject) => {
            RfiAxios.getFileUploadUrl(payload.rfiId, payload.fileName)
                .then(response => {
                    if (!response.data.success) {
                        reject({ message: response.data.message });
                    }

                    resolve(response);
                })
                .catch(error => {
                    reject(error);
                });
        });
    },

    /**
     * Posts new RFI request.
     *
     * @param commit Vuex commit function
     * @param state Vuex module state
     * @param payload Message data
     *
     * @returns {Promise<unknown>}
     */
    postConversation ({ commit, state }, payload) {
        return new Promise((resolve, reject) => {
            RfiAxios.postConversation(state.id, payload.recipients, payload.message, payload.attachments)
                .then(response => {
                    if (!response.data.success) {
                        reject({ message: response.data.message });
                    }

                    commit('UPDATE_RFI_CONVERSATION', response.data.data);
                    commit('SET_RFI_INPUT_ERRORS', {});

                    resolve(response);
                })
                .catch(error => {
                    if (error.response.status === 422) {
                        commit('SET_RFI_INPUT_ERRORS', error.response.data.data);
                    }

                    reject(error);
                });
        });
    },

    /**
     * Posts new message to RFI conversation.
     *
     * @param commit Vuex commit function
     * @param payload Message data
     *
     * @returns {Promise<unknown>}
     */
    postMessage ({ commit }, payload) {
        return new Promise((resolve, reject) => {
            RfiAxios.postMessage(payload.conversationId, payload.message, payload.attachments, payload.isRfi)
                .then(response => {
                    if (!response.data.success) {
                        reject({ message: response.data.message });
                    }

                    commit('UPDATE_RFI_CONVERSATION', response.data.data);
                    commit('SET_RFI_INPUT_ERRORS', {});

                    resolve(response);
                })
                .catch(error => {
                    if (error.response.status === 422) {
                        commit('SET_RFI_INPUT_ERRORS', error.response.data.data);
                    }

                    reject(error);
                });
        });
    },

    /**
     * Posts mark messages as seen.
     *
     * @param commit Vuex commit function
     * @param conversationId RFI conversation ID
     *
     * @returns {Promise<unknown>}
     */
    postMessagesSeen ({ commit }, conversationId) {
        return new Promise((resolve, reject) => {
            RfiAxios.postMessagesSeen(conversationId)
                .then(response => {
                    if (!response.data.success) {
                        reject({ message: response.data.message });
                    }

                    commit('UPDATE_RFI_CONVERSATION', response.data.data);

                    resolve(response);
                })
                .catch(error => {
                    reject(error);
                });
        });
    },

    /**
     * Sets loading state.
     *
     * @param commit Vuex commit function
     * @param isLoading Is loading
     */
    setLoading ({ commit }, isLoading) {
        commit('SET_RFI_LOADING', isLoading);
    },

    /**
     * Sets active conversation
     *
     * @param commit Vuex commit function
     * @param conversationId Conversation ID
     */
    setActiveConversation ({ commit }, conversationId) {
        commit('SET_RFI_ACTIVE_CONVERSATION', conversationId);
    },
};
